<template>
  <div class="home">
    <section class="hero section-pad position-relative">
      <div class="container-nm position-relative">
        <div class="row justify-content-center">
          <div class="col-xl-10 text-center">
            <div class="hero__logo-wrap mb-4" data-nav-hero-sentinel>
              <img src="/logo.svg" alt="NODEMOS" class="hero__logo" />
            </div>

            <p class="nm-tagline mb-4">{{ tagline }}</p>

            <h1 class="hero__headline mb-4">
              {{ home.hero.headline }}
              <span class="hero__headline-accent">{{ home.hero.headlineAccent }}</span>
            </h1>

            <p class="hero__lead mx-auto mb-5">{{ home.hero.lead }}</p>

            <div class="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-5">
              <RouterLink :to="home.hero.primaryCta.to" class="nm-btn nm-btn--primary">
                <i :class="home.hero.primaryCta.icon" aria-hidden="true" />
                {{ home.hero.primaryCta.label }}
              </RouterLink>
              <RouterLink :to="home.hero.secondaryCta.to" class="nm-btn nm-btn--ghost">
                <i :class="home.hero.secondaryCta.icon" aria-hidden="true" />
                {{ home.hero.secondaryCta.label }}
              </RouterLink>
            </div>

            <TrustBar :items="home.trust" />
          </div>
        </div>
      </div>
    </section>

    <section class="section-band section-pad pt-0">
      <div class="container-nm">
        <div class="section-band__inner d-flex flex-column gap-5">
          <FeatureRow
            v-for="pillar in home.pillars"
            :key="pillar.title"
            :eyebrow="pillar.eyebrow"
            :title="pillar.title"
            :body="pillar.body"
            :highlight="pillar.highlight"
            :cta="pillar.cta"
            :visual="pillar.visual"
            :reversed="pillar.reversed"
          />
        </div>
      </div>
    </section>

    <section class="section-pad pt-0">
      <div class="container-nm">
        <QuoteBanner :text="philosophy" />
      </div>
    </section>

    <section class="section-pad pt-0">
      <div class="container-nm">
        <SectionHeader
          :eyebrow="home.audience.eyebrow"
          :title="home.audience.title"
          :lead="home.audience.lead"
        />
        <div class="row g-4">
          <div
            v-for="item in audiences"
            :key="item.title"
            class="col-md-6 col-lg-4"
          >
            <NeuCard
              :featured="item.featured"
              :chip="item.chip"
              :chip-icon="item.chipIcon"
              :title="item.title"
              :body="item.body"
              :cta="item.cta"
              :to="item.to"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="section-band section-pad">
      <div class="container-nm">
        <SectionHeader
          :eyebrow="home.experienceTeaser.eyebrow"
          :title="home.experienceTeaser.title"
          :lead="home.experienceTeaser.lead"
          centered
        />
        <div class="row g-4 mb-5">
          <div
            v-for="portal in experience.items"
            :key="portal.id"
            class="col-md-4"
          >
            <ExperienceTile
              :chip="portal.chip"
              :chip-icon="portal.chipIcon"
              :title="portal.title"
              :body="portal.body"
              :audience="portal.audience"
              :status-label="experience.status"
            />
          </div>
        </div>
        <div class="text-center">
          <RouterLink :to="home.experienceTeaser.cta.to" class="nm-btn nm-btn--ghost">
            <i :class="home.experienceTeaser.cta.icon" aria-hidden="true" />
            {{ home.experienceTeaser.cta.label }}
          </RouterLink>
        </div>
      </div>
    </section>

    <section class="section-pad pt-0">
      <div class="container-nm">
        <SectionHeader
          :eyebrow="home.capabilities.eyebrow"
          :title="home.capabilities.title"
          :lead="home.capabilities.lead"
          centered
        />
        <div class="row g-4">
          <div
            v-for="cap in capabilities"
            :key="cap.title"
            class="col-md-6 col-lg-4"
          >
            <CardSurface pad="md" class="cap-tile h-100">
              <div class="cap-tile__icon mb-3">
                <i :class="cap.icon" aria-hidden="true" />
              </div>
              <h3 class="cap-tile__title mb-2">{{ cap.title }}</h3>
              <p class="cap-tile__body mb-0">{{ cap.body }}</p>
            </CardSurface>
          </div>
        </div>
      </div>
    </section>

    <section class="section-pad pt-0">
      <div class="container-nm">
        <CardSurface raised pad="xl" class="cta-block text-center">
          <div>
            <h2 class="nm-section-title mb-3">{{ home.cta.title }}</h2>
            <p class="nm-section-lead mx-auto mb-4">{{ home.cta.lead }}</p>
            <div class="d-flex flex-column flex-sm-row gap-3 justify-content-center">
              <RouterLink :to="home.cta.primary.to" class="nm-btn nm-btn--primary">
                <i :class="home.cta.primary.icon" aria-hidden="true" />
                {{ home.cta.primary.label }}
              </RouterLink>
              <RouterLink :to="home.cta.secondary.to" class="nm-btn nm-btn--ghost">
                <i :class="home.cta.secondary.icon" aria-hidden="true" />
                {{ home.cta.secondary.label }}
              </RouterLink>
            </div>
          </div>
        </CardSurface>
      </div>
    </section>
  </div>
</template>

<script setup>
import TrustBar from '@/components/TrustBar.vue'
import NeuCard from '@/components/NeuCard.vue'
import CardSurface from '@/components/CardSurface.vue'
import ExperienceTile from '@/components/ExperienceTile.vue'
import QuoteBanner from '@/components/QuoteBanner.vue'
import SectionHeader from '@/components/layout/SectionHeader.vue'
import FeatureRow from '@/components/layout/FeatureRow.vue'
import {
  tagline,
  philosophy,
  home,
  audiences,
  capabilities,
  experience,
} from '@/content/site'
</script>

<style scoped>
.hero {
  padding-top: clamp(3rem, 8vw, 6rem);
}

.hero__logo-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.hero__logo {
  width: min(420px, 72vw);
  max-width: 100%;
  height: auto;
  display: block;
  margin-inline: auto;
}

.hero__headline {
  font-size: clamp(2rem, 5.5vw, 3.5rem);
  font-weight: var(--nm-font-weight-bold);
  line-height: 1.08;
  letter-spacing: -0.03em;
}

.hero__headline-accent {
  display: block;
  margin-top: 0.12em;
  background: linear-gradient(90deg, var(--nm-accent-glow), var(--nm-accent));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero__lead {
  max-width: 40rem;
  font-size: 1.12rem;
  line-height: 1.75;
  color: var(--nm-text-muted);
}

.cap-tile__icon {
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--nm-radius-sm);
  background: var(--nm-surface-inset);
  box-shadow: var(--nm-inset-sm);
  color: var(--nm-accent-glow);
  font-size: 1.15rem;
}

.cap-tile__title {
  font-size: 1.05rem;
  font-weight: var(--nm-font-weight-semibold);
}

.cap-tile__body {
  font-size: 0.9rem;
  color: var(--nm-text-muted);
  line-height: 1.55;
}


</style>